import { Link, useLocation } from 'react-router-dom';
import { useSession, type KybStatus, type Me } from '~/lib/session-context';

/**
 * Impossible-to-miss nudge whose copy follows BOTH signals:
 *   tenant.status — pending_kyb / suspended / active
 *   kybStatus     — none / submitted / in_review / approved / rejected
 *
 * Tenant.status stays pending_kyb until a reviewer approves, so a tenant
 * who just submitted still reads pending — the kybStatus tells us what to
 * actually say. Hidden on /kyb itself (user is already in the right place)
 * and on an active tenant (nothing to nag about).
 */
export function VerificationBanner(): JSX.Element | null {
  const { me, kybStatus } = useSession();
  const { pathname } = useLocation();

  if (!me) return null;
  if (me.tenant.status === 'active') return null;
  if (pathname === '/kyb') return null;

  const config = bannerCopy(me.tenant.status, kybStatus);
  if (!config) return null;

  return (
    <div
      role="status"
      className={`border-b border-rule-hairline px-10 py-3 ${config.tone}`}
    >
      <div className="mx-auto flex w-full max-w-[1280px] items-start justify-between gap-6">
        <div className="flex items-start gap-3">
          {config.icon === 'warn' ? <WarnIcon /> : <ClockIcon />}
          <div>
            <div className="text-[14.5px] font-semibold leading-[18px]">
              {config.headline}
            </div>
            <p className="mt-0.5 text-[13.5px] leading-[18px] opacity-80">
              {config.sub}
            </p>
          </div>
        </div>
        <Link
          to="/kyb"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 self-center rounded-md bg-ink-primary px-3 text-[13.5px] font-semibold text-ink-on-dark hover:bg-ink-primary/90"
        >
          {config.cta}
          <svg
            width="10"
            height="10"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8h10M9 4l4 4-4 4" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

interface BannerConfig {
  tone: string;
  icon: 'warn' | 'clock';
  headline: string;
  sub: string;
  cta: string;
}

function bannerCopy(
  tenantStatus: Me['tenant']['status'],
  kyb: KybStatus,
): BannerConfig | null {
  if (tenantStatus === 'suspended') {
    return {
      tone: 'bg-stop-tint text-stop border-stop/25',
      icon: 'warn',
      headline: 'Business verification revoked',
      sub: "A reviewer flagged your account. You can't issue cards or mint live keys until this clears.",
      cta: 'Review and resubmit',
    };
  }

  // tenantStatus === 'pending_kyb' from here on.
  if (kyb === 'submitted' || kyb === 'in_review') {
    return {
      tone: 'bg-footnote-tint text-footnote border-footnote/25',
      icon: 'clock',
      headline:
        kyb === 'in_review'
          ? 'A reviewer is looking at your file'
          : 'Verification submitted — in review',
      sub: 'Most submissions clear within one business day. We will email the owner on file with the decision.',
      cta: 'Check status',
    };
  }
  if (kyb === 'rejected') {
    return {
      tone: 'bg-stop-tint text-stop border-stop/25',
      icon: 'warn',
      headline: "We couldn't approve your last submission",
      sub: 'See the reviewer note on the verification page, then resubmit with the fix.',
      cta: 'Review and resubmit',
    };
  }
  if (kyb === 'approved') {
    // The tenant row hasn't propagated yet; the chrome will catch up on the
    // next /me poll. Say so plainly rather than looking like we're nagging.
    return {
      tone: 'bg-tally-tint text-tally border-tally/25',
      icon: 'clock',
      headline: 'Verification approved — opening live access',
      sub: 'Refreshing to switch to live mode. If this persists, reload the page.',
      cta: 'Open verification',
    };
  }
  // No case yet.
  return {
    tone: 'bg-notice-tint text-notice border-notice/25',
    icon: 'warn',
    headline: 'Business verification pending',
    sub: 'Submit your business documents to issue live cards and mint live API keys.',
    cta: 'Start verification',
  };
}

function WarnIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-[1px] flex-none"
      aria-hidden
    >
      <path d="M8 3L1.5 13h13L8 3z" />
      <path d="M8 7v3M8 12v.5" />
    </svg>
  );
}

function ClockIcon(): JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-[1px] flex-none"
      aria-hidden
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 4.5V8l2.25 1.5" />
    </svg>
  );
}
